import React, { useContext, useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BigModal from '../../../../components/Modals/BigModal'
import SmallModal from '../../../../components/Modals/SmallModal'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'

// Edit / delete a document attached to an event. `document` doubles as the
// open flag - pass null to close.
const EditDocumentDialog = ({
  eventID,
  document,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const { setToast } = useContext(GlobalContext)
  const [name, setName] = useState('')
  const [visibleToAllStaff, setVisibleToAllStaff] = useState(false)
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (document) {
      setName(document.documentName || '')
      setVisibleToAllStaff(!!document.visibleToAllStaff)
      setOpenConfirmDelete(false)
      setBusy(false)
    }
  }, [document])

  if (!document) return null

  const unchanged =
    name.trim() === (document.documentName || '') &&
    visibleToAllStaff === !!document.visibleToAllStaff

  // The API client never rejects on 4xx/5xx - check the status
  const handleSave = async (e) => {
    e.preventDefault()
    setBusy(true)
    const res = await API.post('/event/edit-document', {
      eventID,
      docID: document._id,
      documentName: name.trim(),
      visibleToAllStaff,
    })
    setBusy(false)
    if (res.status === 200) {
      setToast({ message: 'Document updated' })
      onSaved({
        ...document,
        documentName: name.trim(),
        visibleToAllStaff,
      })
      onClose()
    } else {
      setToast({ message: 'Unable to update this document', severity: 'error' })
    }
  }

  const handleDelete = async () => {
    setBusy(true)
    const res = await API.post('/event/delete-document', {
      eventID,
      docID: document._id,
    })
    setBusy(false)
    if (res.status === 200) {
      setToast({ message: 'Document deleted' })
      onDeleted(document._id)
      onClose()
    } else {
      setToast({ message: 'Unable to delete this document', severity: 'error' })
    }
  }

  return (
    <BigModal autoWidth open onClose={onClose}>
      <Stack
        gap={2}
        component="form"
        onSubmit={handleSave}
        sx={{ minWidth: { sm: 380 } }}
      >
        <Stack gap={0.25}>
          <Typography variant="title3">Edit document</Typography>
          <Typography variant="caption" color="text.secondary">
            Changes are recorded in the event activity log.
          </Typography>
        </Stack>
        <TextField
          fullWidth
          label="Document name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Stack gap={0}>
          <FormControlLabel
            label="Viewable by all staff"
            control={
              <Switch
                checked={visibleToAllStaff}
                onChange={(e) => setVisibleToAllStaff(e.target.checked)}
                color="success"
              />
            }
          />
          <Typography variant="caption" color="text.secondary">
            {visibleToAllStaff
              ? 'Shown to staff on the read-only event view and in the mobile app.'
              : 'Hidden from staff without the Event Planner permission.'}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1} justifyContent="space-between">
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setOpenConfirmDelete(true)}
            disabled={busy}
          >
            Delete
          </Button>
          <Stack direction="row" gap={1}>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={busy || unchanged || !name.trim()}
            >
              Save Changes
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <SmallModal
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
      >
        <Stack gap={1}>
          <Typography variant="title3" textAlign="center">
            Are you sure you want to delete this document?
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            "{document.documentName || 'Untitled document'}" will be removed
            from this event. This cannot be undone.
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={busy}
          >
            Yes - Delete
          </Button>
          <Button
            variant="outlined"
            onClick={() => setOpenConfirmDelete(false)}
            disabled={busy}
          >
            No - Cancel
          </Button>
        </Stack>
      </SmallModal>
    </BigModal>
  )
}

export default EditDocumentDialog
